/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect, useRef } from "react";
// Only the grid is still Argon's. The card, fields and buttons are now the
// brand-auth primitives — see styles/brand-auth.css.
import { Container, Row, Col } from "reactstrap";
import "../../styles/brand-auth.css";
import { loginUserSessionService, requestCourseCheckout } from "../../services/userService"
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { ENROLLMENT_OPEN } from "../../config/enrollment";
import { SHOW_GOOGLE_SIGN_IN } from "../../config/googleAuth";
import {
  COURSE_ELIGIBILITY,
  OTHER_COUNTRY,
  docLangFor,
  isCourseCountry,
  legalAcceptance,
  privacyNoticeFor,
} from "../../config/legal";
import {
  LegalCheckbox,
  LegalLinks,
  SignupConsentLabel,
  SignupCountryField,
} from "components/Legal/LegalConsent";
import { registerErrorKey } from "../../utils/registerErrors";
import useReveal from "hooks/useReveal";

// The field each error is about, by the id of its input.
const FIELD_IDS = {
  country: "rtp-country",
  legal: "rtp-legal",
  course: "rtp-course-eligible",
};
const COUNTRY_NOTICE_ID = "rtp-country-notice";

const RegisterToPlan = () => {
  // The card's entrance — see styles/premium.css. Called up here, before the
  // enrollment-closed early return, so both branches get it.
  useReveal();
  const navigate = useNavigate();
  // Post-payment destination forwarded from the subscriptions page (?next=...).
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "";
  const [state, setState] = useState({
    email: "",
    password: "",
    name: "",
    last: "",
    error: null,
    message: null,
  });
  const { signUp } = useUserAuth();
  const { t, i18n } = useTranslation();
  const mainRef = useRef(null);
  // The 18+ / Terms box for the account, and the 18+ / US box for the
  // course purchase. Never pre-ticked.
  const [legalOk, setLegalOk] = useState(false);
  const [courseOk, setCourseOk] = useState(false);
  // A code from SIGNUP_COUNTRIES, OTHER_COUNTRY, or "" before a choice.
  // Courses are sold in the US and its territories only, so Mexico and Costa
  // Rica stop here, before any account is created.
  const [country, setCountry] = useState("");
  // Which field the error is about: it is marked invalid and gets focus.
  const [errorField, setErrorField] = useState(null);
  const countryNotice =
    country === OTHER_COUNTRY ? "legal.signupCountryNotServed"
      : country && !isCourseCountry(country) ? "legal.courseCountryNotSold"
        : null;

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainRef.current.scrollTop = 0;
  }, []);

  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const fail = (message, field = null) => {
    setState({ ...state, error: message, message: null });
    setErrorField(field);
    if (field) document.getElementById(FIELD_IDS[field])?.focus();
  };

  // aria-invalid and aria-describedby for one field: its hint, if any, and
  // the error message while the error is about this field.
  const fieldA11y = (field, hintId) => {
    const invalid = Boolean(state.error) && errorField === field;
    const describedBy = [hintId, invalid && "rtp-error"].filter(Boolean).join(" ");
    return {
      "aria-invalid": invalid || undefined,
      "aria-describedby": describedBy || undefined,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // The form isn't rendered while enrollment is closed, so this is only a
    // guard against creating an account we'd immediately fail to charge.
    if (!ENROLLMENT_OPEN) return;

    const { email, password, name, last } = state;

    if (!country) return fail(t("legal.signupCountryRequired"), "country");
    if (country === OTHER_COUNTRY) return fail(t("legal.signupCountryNotServed"), "country");
    if (!isCourseCountry(country)) return fail(t("legal.courseCountryNotSold"), "country");
    if (!legalOk) return fail(t("legal.signupConfirmRequired"), "legal");
    if (!courseOk) return fail(t("legal.eligibilityRequired"), "course");

    setState({ ...state, error: null, message: null });
    setErrorField(null);
    const locale = (i18n.language || "es").startsWith("en") ? "en" : "es";

    // The step that failed picks the message: sign-up errors come from
    // Firebase, checkout errors from the backend.
    let signedUp = false;
    try {
      const loggedUser = await signUp(
        email, password, "user", name, last,
        legalAcceptance({ residenceCountry: country, locale, privacyNotice: privacyNoticeFor(country) })
      );
      // signUp resolves to nothing when linking a guest account fails (it logs
      // and swallows that error): a sign-up failure, not a checkout one.
      if (!loggedUser) throw new Error("signup failed");
      signedUp = true;

      await loginUserSessionService(loggedUser.accessToken)

      const responseURL = await requestCourseCheckout(loggedUser.uid, loggedUser.email, nextPath, {
        eligibility: COURSE_ELIGIBILITY,
        locale,
      });

      if (!responseURL) {
        throw new Error("Could not start checkout. Please try again.");
      }

      window.location.href = responseURL;

      setState({
        ...state,
        message: "User registered successfully!",
        error: null,
      });
    } catch (error) {
      let message;
      if (error?.code === "eligibility_required") message = t("legal.eligibilityRequired");
      else if (error?.code === "course_country_not_sold") message = t("legal.courseCountryNotSold");
      else if (signedUp) message = t("legal.checkoutError");
      else message = t(registerErrorKey(error));
      setState({ ...state, error: message, message: null });
    }
  };

  // Enrollment closed (production, while the course is still being finished).
  // Only reachable by direct URL now that /subscriptions hides its CTA, but a
  // sign-up form that ends at Stripe must not be one of the things it shows.
  if (!ENROLLMENT_OPEN) {
    return (
      <>
        <DemoNavbar />
        <main className="cn-auth" ref={mainRef}>
          <section className="cn-auth-hero cn-grid">
            <span className="cn-grain" aria-hidden="true" />
            <span className="cn-orb cn-orb-1" />
            <span className="cn-orb cn-orb-2" />
            <span className="cn-orb cn-orb-3" />
            <Container>
              <Row className="justify-content-center">
                <Col xs="12" md="10" lg="7">
                  <div className="cn-card text-center" data-reveal>
                    <span className="cn-eyebrow">
                      <span className="cn-dot" />
                      {t("enrollment.comingSoonEyebrow")}
                    </span>
                    <h1 className="cn-title">{t("enrollment.comingSoonTitle")}</h1>
                    <p className="cn-sub mb-4">{t("enrollment.comingSoonBody")}</p>
                    <button
                      className="cn-btn-primary"
                      type="button"
                      onClick={() => navigate("/")}
                    >
                      {t("enrollment.backHomeCta")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        </main>
        <MainFooter />
      </>
    );
  }

  return (
    <>
      <DemoNavbar />
      <main className="cn-auth" ref={mainRef}>
        <section className="cn-auth-hero cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
          <Container>
            <Row className="justify-content-center">
              <Col xs="12" md="10" lg="6" xl="5">
                <div className="cn-card" data-reveal>
                  <div className="cn-card-head">
                    <h1 className="cn-title">{t("register.createAccount")}</h1>
                  </div>

                  {/* Still the Creative Tim no-op buttons behind the flag, which
                      is off. Wiring them up is a separate job from restyling. */}
                  {SHOW_GOOGLE_SIGN_IN && (
                    <>
                      <button
                        className="cn-social"
                        type="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        <img
                          alt=""
                          aria-hidden="true"
                          src={
                            require("assets/img/icons/common/google.svg")
                              .default
                          }
                        />
                        <span>{t("register.signinWith")} Google</span>
                      </button>
                      <div className="cn-divider">
                        {t("register.orSigninWith")}
                      </div>
                    </>
                  )}

                  <form onSubmit={handleSubmit} role="form">
                    <Row>
                      <Col xs="12" sm="6">
                        <div className="cn-field">
                          <label className="cn-label" htmlFor="rtp-name">
                            {t("register.name")}
                          </label>
                          <div className="cn-input-wrap">
                            <i className="ni ni-hat-3 cn-input-icon" aria-hidden="true" />
                            <input
                              id="rtp-name"
                              className="cn-input"
                              name="name"
                              type="text"
                              autoComplete="given-name"
                              placeholder={t("register.name")}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs="12" sm="6">
                        <div className="cn-field">
                          <label className="cn-label" htmlFor="rtp-last">
                            {t("register.last")}
                          </label>
                          <div className="cn-input-wrap">
                            <i className="ni ni-hat-3 cn-input-icon" aria-hidden="true" />
                            <input
                              id="rtp-last"
                              className="cn-input"
                              name="last"
                              type="text"
                              autoComplete="family-name"
                              placeholder={t("register.last")}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="cn-field">
                      <label className="cn-label" htmlFor="rtp-email">
                        {t("register.email")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-email-83 cn-input-icon" aria-hidden="true" />
                        <input
                          id="rtp-email"
                          className="cn-input"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder={t("register.email")}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* Dropped here, not moved: a hard-coded "password
                        strength: strong" line that said "strong" for every
                        password, and a "Privacy Policy" consent checkbox whose
                        link was Creative Tim's #pablo placeholder and whose
                        checked state was never read. Both were template
                        leftovers; the strength line was actively misleading.
                        The real consent box is below the password. */}
                    <div className="cn-field">
                      <label className="cn-label" htmlFor="rtp-password">
                        {t("register.password")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-lock-circle-open cn-input-icon" aria-hidden="true" />
                        <input
                          id="rtp-password"
                          className="cn-input"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                          placeholder={t("register.password")}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <SignupCountryField
                      last
                      id={FIELD_IDS.country}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      describedBy={fieldA11y("country", countryNotice ? COUNTRY_NOTICE_ID : null)["aria-describedby"]}
                      invalid={fieldA11y("country")["aria-invalid"]}
                    />
                    {countryNotice && (
                      <p className="cn-hint" id={COUNTRY_NOTICE_ID}>{t(countryNotice)}</p>
                    )}

                    {/* Two boxes: this submit signs up (the account box) AND
                        starts the course checkout, which is sold in the US
                        only (the 18+ / US box, the same one /subscriptions
                        shows). */}
                    <LegalCheckbox
                      id={FIELD_IDS.legal}
                      checked={legalOk}
                      onChange={(e) => setLegalOk(e.target.checked)}
                      describedBy={fieldA11y("legal")["aria-describedby"]}
                      invalid={fieldA11y("legal")["aria-invalid"]}
                    >
                      <SignupConsentLabel docLang={docLangFor(country)} />
                    </LegalCheckbox>
                    <LegalCheckbox
                      id={FIELD_IDS.course}
                      checked={courseOk}
                      onChange={(e) => setCourseOk(e.target.checked)}
                      describedBy={fieldA11y("course", "rtp-course-eligible-hint")["aria-describedby"]}
                      invalid={fieldA11y("course")["aria-invalid"]}
                    >
                      {t("legal.eligibilityConfirm")}
                    </LegalCheckbox>
                    <p className="cn-legal-links" id="rtp-course-eligible-hint">
                      {t("legal.usOnlyNote")}
                    </p>

                    {state.error && (
                      <div className="cn-error" role="alert" id="rtp-error">
                        {state.error}
                      </div>
                    )}

                    <button className="cn-btn-primary cn-btn-block cn-btn-arrow" type="submit">
                      {t("register.createAccount")}
                    </button>
                    <LegalLinks docLang={docLangFor(country)} />
                  </form>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default RegisterToPlan;
